import React from 'react';
import styled, { css } from 'styled-components';
import { NAVBAR_HEIGHT } from '../Navbar';
import { PExtraSmall } from '../elements/Text';
import Markdown from '../Markdown';
import { MultiLineInput } from '../elements/Input';

export const SectionSnap = styled.div`
  scroll-snap-align: start;
  @supports (font: -apple-system-body) and (-webkit-appearance: none) {
    scroll-snap-align: start end;
    // obviously we needed a hack for safari :) because safari wont lock into the end by default
  }

  margin-top: 32px;
  scroll-margin-top: 32px;
`;

export const EmptyDiv = styled.div<{ height?: number }>`
  height: ${({ height }) => height || 72}px;
`;

export const StepScreen = styled.div`
  height: calc(100vh - ${NAVBAR_HEIGHT}px);
  overflow-x: hidden;
`;
export const SectionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 24px;
`;
export const SectionWrapper = styled.div`
  /* min-height: 42vh; */
  margin-bottom: 150px;
`;
export const StepWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: calc(100vh - 2 * ${NAVBAR_HEIGHT}px);

  overflow-x: hidden;
`;

export const StepReadableContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 820px;
  width: 100%;
  margin: 0 auto;
`;

export const StepScrollWrapper = styled.div`
  overflow: hidden;
  height: 100%;
  padding: 0px;
  width: 100%;
  box-sizing: border-box;
`;

export const OptionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 32px;
  width: 100%;
  justify-content: flex-start;
  padding-bottom: 12px;
`;

export const Option = styled.div<{ isSelected: boolean }>`
  padding: 8px;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  padding: 8px 16px;
  color: ${({ theme }) => theme.colors.textPrimary};
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  font-size: 16px;
  border-radius: 15px;
  transition: all 0.3s ease-in-out;

  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      border-color: ${theme.colors.primary};
      background-color: ${theme.colors.inputBackground};
    `}

  &:hover {
    background-color: ${({ theme }) => theme.colors.inputBackground};
  }
`;

export const WideOptionsContainer = styled(OptionsContainer)`
  flex-direction: column;
  flex-wrap: nowrap;
`;

export const WideOption = styled(Option)`
  width: 100%;
  flex: 1;
`;

export const StaticOption = styled(Option)<{ isSelected: boolean }>`
  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      border: 1px solid ${theme.colors.textPrimary};
      background-color: ${theme.colors.inputBackground};
    `}
`;

const SplitScreenStepWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  width: 100%;
  height: calc(100vh - 2 * ${NAVBAR_HEIGHT}px);
  position: relative;
`;

const SplitScreenLeftWrapper = styled.div`
  width: 50%;
  flex: 1;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  padding: 0 64px;
  overflow-y: auto;
  height: calc(100vh - 2 * ${NAVBAR_HEIGHT}px);
`;
export const NonCopyableMarkdown = styled(Markdown)`
  user-select: none;

  .table-wrapper {
    border-radius: 8px;
    padding-bottom: 0;
    border: 1px solid ${({ theme }) => theme.colors.bgActive};
    background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
    scrollbar-width: thin;

    // hack to make the scrollbar not overlap the border radius
    mask-image: radial-gradient(
        circle at 0 100%,
        transparent 0,
        transparent 8px,
        black 8px
      ),
      /* Bottom-left */
        radial-gradient(
          circle at 100% 100%,
          transparent 0,
          transparent 8px,
          black 8px
        ); /* Bottom-right */
    mask-size: 100% 100%;
    mask-repeat: no-repeat;
  }

  table {
    min-width: 100%;
    background-color: transparent;
    border-collapse: inherit;
    border-spacing: 0px;

    thead > tr:last-child > th {
      border-bottom: 1px solid ${({ theme }) => theme.colors.bgActive};
    }
  }

  pre > pre {
    background-color: ${({ theme }) =>
      theme.colors.bgDefaultSecondary} !important;
    border: 1px solid ${({ theme }) => theme.colors.bgActive};
    code {
      scrollbar-width: thin;
    }
  }
`;

export const NonCopyableSpan = styled.span`
  user-select: none;
`;

export const NonCopyableMarkdownOption = styled(Markdown)`
  user-select: none;
  * {
    font-size: 16px;
    margin-top: 0;
    padding-bottom: 0;
  }
`;

const SplitScreenRightWrapper = styled.div`
  width: 50%;
  flex: 1;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 2 * ${NAVBAR_HEIGHT}px);
`;

export function SplitScreenStep({
  children,
}: {
  children: [React.ReactNode, React.ReactNode];
}) {
  return (
    <SplitScreenStepWrapper>
      <SplitScreenLeftWrapper>{children[0]}</SplitScreenLeftWrapper>
      <SplitScreenRightWrapper>{children[1]}</SplitScreenRightWrapper>
    </SplitScreenStepWrapper>
  );
}

export const Label = styled(PExtraSmall)`
  color: ${({ theme }) => theme.colors.textPlaceholder};
  font-weight: 400;
  margin-top: 6px;
`;

export function NonPastableTextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <>
      <MultiLineInput
        placeholder={placeholder ?? 'Write your answer here'}
        value={value}
        minRows={5}
        maxRows={15}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        onPaste={(e) => {
          e.preventDefault();
          return false;
        }}
        style={{
          marginTop: 24,
        }}
      />
      <Label>Press ↵ for a new line</Label>
    </>
  );
}

export const MatchingColumnsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 52px;
  margin-top: 24px;
  padding-bottom: 12px;
  align-items: center;
`;

export const MatchingColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const NAVIGATION_WIDTH = '280px';
const NAVIGATION_MARGIN = '62px';

export const FloatingNavigationContainer = styled.div`
  position: absolute;
  top: 32px;
  bottom: calc(${NAVBAR_HEIGHT}px + 32px);
  left: ${NAVIGATION_MARGIN};
  width: ${NAVIGATION_WIDTH};
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

export const FloatingNavigationCard = styled.div`
  width: ${NAVIGATION_WIDTH};
  max-height: 100%;
  background-color: #161617;
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadows.default};
  transition: all 0.3s ease-in-out;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  pointer-events: auto;
`;

export const NavigationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.bgActive};
  flex-shrink: 0;
`;

export const NavigationTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

export const NavigationSubtitle = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textRegular};
`;

export const NavigationContent = styled.div`
  padding: 16px 16px 14px 16px;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.bgActive} transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    margin: 4px 0;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.bgActive};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.textRegular};
  }
`;

export const NavigationItem = styled.div<{
  isActive: boolean;
  isAnswered?: boolean | null;
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  margin-bottom: 3px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  font-size: 13px;
  color: ${({ theme, isActive }) =>
    isActive ? theme.colors.textActive : theme.colors.textRegular};
  background-color: ${({ theme, isActive }) =>
    isActive ? theme.colors.bgActive : 'transparent'};
  border: 1px solid
    ${({ theme, isActive }) =>
      isActive ? theme.colors.textActive : 'transparent'};

  &:hover {
    background-color: ${({ theme, isActive }) =>
      isActive ? theme.colors.bgActive : theme.colors.bgDefault};
    color: ${({ theme }) => theme.colors.textActive};
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

export const NavigationItemText = styled.span`
  font-weight: ${({ isActive }: { isActive: boolean }) =>
    isActive ? '600' : '400'};
`;

export const StatusDot = styled.span<{
  isAnswered: boolean;
  isActive: boolean;
}>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 2px solid
    ${({ theme, isAnswered }) =>
      isAnswered ? theme.colors.primary : theme.colors.textRegular};
  background-color: ${({ theme, isAnswered }) =>
    isAnswered ? theme.colors.primary : 'transparent'};
  box-shadow: ${({ isActive, theme }) =>
    isActive ? `0 0 0 2px ${theme.colors.bgActive}` : 'none'};
`;

export const ContentWrapper = styled.div`
  transition: all 0.2s ease-in-out;
  margin-left: calc(${NAVIGATION_WIDTH} + ${NAVIGATION_MARGIN});
  display: flex;
  justify-content: center;
  width: calc(100vw - ${NAVIGATION_WIDTH} - ${NAVIGATION_MARGIN});
  max-width: 100vw;
  overflow-x: hidden;
  overflow-y: auto;

  /* scroll-snap-type: y mandatory;
  @supports (font: -apple-system-body) and (-webkit-appearance: none) {
    scroll-snap-type: y proximity;
    // obviously we needed a hack for safari :) because the "end" part of "scroll-snap-align: start end" is not supported
  } */
  height: 100%;

  /* Prevent scroll chaining to body during interactions (e.g., drag) */
  overscroll-behavior: contain;

  /* Disable snap while dragging to avoid jumpy behavior when leaving container */
  &.drag-active {
    scroll-snap-type: none;
  }
`;

export const ContentContainer = styled.div`
  max-width: 820px;
  width: 100%;
  margin: 0 auto;
  padding: 0 40px;
  box-sizing: border-box;
`;

export const NavigationFooter = styled.div`
  padding: 16px 16px 14px 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.bgActive};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
`;

export const FooterRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

export const Legend = styled.div``;
